// spreadsheetReader.js
// Excel (.xlsx, .xls) ve ODS (.ods) dosyalarını ortak şekilde okuyan modül.
// excelOperations.js ve odsOperations.js bu modülün ince wrapper'larıdır.
import * as XLSX from 'xlsx';

export const getColumnIndex = (headers, columnMappings, key) => {
  const possibleHeaders = columnMappings[key] || [key];
  return headers.findIndex(header =>
    header && typeof header === 'string' && possibleHeaders.some(possibleHeader =>
      header.toLowerCase().includes(possibleHeader.toLowerCase())
    )
  );
};

export const formatRow = (rowData, index, headers, columnMappings = {}) => {
  const getValue = (key) => {
    const idx = getColumnIndex(headers, columnMappings, key);
    if (idx === -1) return '';
    const v = rowData[idx];
    return v === undefined || v === null ? '' : v;
  };

  // Hakim adı içeren alanlarda baş/son boşluk + ardışık iç boşluk Excel'den
  // sık geliyor; bu da manuel/eski hakim eşleştirmesini bozar. Trim et.
  const trimJudgeName = (v) => {
    if (typeof v !== 'string') return v;
    return v.trim().replace(/\s+/g, ' ');
  };

  return {
    id: index + 1,
    esasNo: getValue('esasNo'),
    sanikSayisi: parseInt(getValue('sanikSayisi')) || 1,
    magdurSayisi: parseInt(getValue('magdurSayisi')) || 1,
    klasorSayisi: getValue('klasorSayisi'),
    gorus: getValue('gorus'),
    sucTuru: getValue('sucTuru'),
    eskiHakim: trimJudgeName(getValue('eskiHakim')),
    manuelTevziEdilecekHakim: trimJudgeName(getValue('manuelTevziEdilecekHakim')),
    manuelTevziHakim: trimJudgeName(getValue('manuelTevziHakim')),
  };
};

/**
 * Bir satırı "boş" (filtreye takılır) sayar mı?
 * Kurallar:
 *  - esasNo hücresi boşsa: satır boş — tablo altındaki büyük boş bölge bu kurala takılır.
 *  - esasNo dolu ama TÜM metin alanları boşsa: defansif (tek boşluk karakterli hücreler).
 * Performans kritik: Excel'in altındaki 1000+ boş satır algoritmaya akarsa balanceBySwaps
 * O(N² × M²) ile çalıştığı için tarayıcı donar; "sonsuz döngü" izlenimi verir.
 */
export const isEmptyDataRow = (row, headers, columnMappings) => {
  if (!Array.isArray(row)) return true;
  const esasNoIdx = getColumnIndex(headers, columnMappings, 'esasNo');
  if (esasNoIdx !== -1) {
    const v = row[esasNoIdx];
    if (v === undefined || v === null || String(v).trim() === '') return true;
  }
  // Bütün hücreler boş ise (toString().trim() === '') de boş say
  const hasAnyValue = row.some((cell) => cell !== undefined && cell !== null && String(cell).trim() !== '');
  return !hasAnyValue;
};

export const filterMeaningfulRows = (dataRows, headers, columnMappings) =>
  dataRows.filter((row) => !isEmptyDataRow(row, headers, columnMappings));

export const findHeaderRowIndex = (jsonData, columnMappings, maxScan = 10) => {
  const requiredHeaders = ['esasNo', 'sanikSayisi', 'magdurSayisi', 'klasorSayisi'];

  for (let i = 0; i < Math.min(maxScan, jsonData.length); i++) {
    const row = jsonData[i];
    if (row && Array.isArray(row) && row.some(cell =>
      cell && typeof cell === 'string' && requiredHeaders.some(header =>
        (columnMappings[header] || [header]).some(possibleHeader =>
          cell.toLowerCase().includes(possibleHeader.toLowerCase())
        )
      )
    )) {
      return i;
    }
  }
  return -1;
};

export const readSpreadsheet = (file, options = {}) => {
  const {
    progressCallback,
    columnMappings = {},
    filterEmpty = false,
    maxHeaderScan = 10,
    readOptions = {},
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', ...readOptions });

        const headerRowIndex = findHeaderRowIndex(jsonData, columnMappings, maxHeaderScan);
        if (headerRowIndex === -1) {
          throw new Error('Geçerli başlık satırı bulunamadı. Lütfen dosyanızın gerekli sütunları içerdiğinden emin olun.');
        }

        const headers = jsonData[headerRowIndex];
        const dataRows = jsonData.slice(headerRowIndex + 1);

        const requiredHeaders = ['esasNo', 'sanikSayisi', 'magdurSayisi', 'klasorSayisi'];
        const missingHeaders = requiredHeaders.filter(header =>
          getColumnIndex(headers, columnMappings, header) === -1
        );

        if (missingHeaders.length > 0) {
          const missingHeadersInfo = missingHeaders.map(header => {
            const possibleHeaders = columnMappings[header] || [header];
            return `${header} (Olası başlıklar: ${possibleHeaders.join(', ')})`;
          }).join('; ');
          throw new Error(`Zorunlu başlıklar eksik: ${missingHeadersInfo}`);
        }

        const filteredRows = filterEmpty ? filterMeaningfulRows(dataRows, headers, columnMappings) : dataRows;

        const totalRows = filteredRows.length;
        const formattedData = filteredRows.map((row, index) => {
          if (progressCallback && totalRows > 0) {
            progressCallback(Math.round((index / totalRows) * 100));
          }
          return formatRow(row, index, headers, columnMappings);
        });

        resolve(formattedData);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};
